import { inject, Injectable } from '@angular/core';
import {
  deleteEntities,
  selectAllEntities,
  selectEntity,
  setEntities,
  upsertEntities,
} from '@ngneat/elf-entities';
import { UserSettingService } from '@thiememeulenhoff/ng-api-gateway';
import { map, Observable, switchMap, tap } from 'rxjs';
import { UserSetting } from '@thiememeulenhoff/types';
import { toSignal } from '@angular/core/rxjs-interop';
import * as stores from './user-setting.store';

@Injectable({
  providedIn: 'root',
})
export class UserSettingFacade {
  readonly #userSettings = stores.userSettings;

  readonly #userSettingService = inject(UserSettingService);

  readonly userSettings$ = this.#userSettings.pipe(selectAllEntities());

  readonly userSettingByKey$ = (key: string) =>
    this.#userSettings.pipe(selectEntity(key)) as Observable<
      UserSetting | undefined
    >;

  readonly userSettingValue = (key: string) =>
    toSignal(
      this.userSettingByKey$(key).pipe(
        map((userSetting) => userSetting?.value),
      ),
    );

  fetchUserSettings() {
    return this.#userSettingService
      .fetchUserSettings()
      .pipe(tap(this.setUserSettings));
  }

  setUserSetting(
    userSetting: UserSetting,
    shouldUpsertFirst = false,
    shouldReload = true,
  ) {
    if (shouldUpsertFirst) {
      this.#upsertUserSettings([userSetting]);
    }

    return this.#userSettingService.createUserSetting(userSetting).pipe(
      tap(() =>
        shouldUpsertFirst ? null : this.#upsertUserSettings([userSetting]),
      ),
      switchMap(() => (shouldReload ? this.fetchUserSettings() : [])),
    );
  }

  deleteUserSetting(
    key: string,
    shouldUpsertFirst = false,
    shouldReload = true,
  ) {
    if (shouldUpsertFirst) {
      this.#deleteUserSetting(key);
    }

    return this.#userSettingService.deleteUserSetting(key).pipe(
      tap(() => this.#deleteUserSetting(key)),
      switchMap(() => (shouldReload ? this.fetchUserSettings() : [])),
    );
  }

  getUserSetting(key: string) {
    return this.#userSettingService.getUserSetting(key).pipe(
      tap((userSetting: UserSetting | null) => {
        if (userSetting) {
          this.#upsertUserSettings([userSetting]);
        }
      }),
    );
  }

  // #region Store mutations
  setUserSettings = (userSettings: Array<UserSetting>) => {
    this.#userSettings.update(setEntities(userSettings));
  };

  // #region Store mutations
  #upsertUserSettings = (userSettings: Array<UserSetting>) => {
    this.#userSettings.update(upsertEntities(userSettings));
  };

  #deleteUserSetting = (key: string) => {
    this.#userSettings.update(deleteEntities(key));
  };
  // #endregion
}
